import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faCompress,
  faCopy,
  faExpand,
  faLink,
  faRotateRight,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import type { CodeToHastOptions } from "shiki";
import { version as markoVersion } from "marko/package.json";
client import highlighter from "app/util/shiki";
client import "terser";
client import { clearLogs, update, type File } from "app/util/workspace";
import { bytesToUnits } from "app/util/sizes";
import { styleToClass } from "app/util/style-to-class";
import styles from "./result.style.module.scss";
export interface Input {
  files: File[];
  selected: number;
}
static const compiledHighlightOpts: CodeToHastOptions = {
  lang: "js",
  defaultColor: false,
  themes: {
    light: "marko-light",
    dark: "marko-dark",
  },
  transformers: [
    {
      pre(t) {
        const className = styleToClass(t.properties.style);
        if (className) {
          t.properties.style = undefined;
          this.addClassToHast(t, className);
        }
      },
      tokens(lines) {
        for (const line of lines) {
          for (const token of line) {
            const className = styleToClass(token.htmlStyle);
            if (className) {
              token.htmlStyle = undefined;
              token.htmlAttrs ||= {};
              if (token.htmlAttrs.class) {
                token.htmlAttrs.class += " " + className;
              } else {
                token.htmlAttrs.class = className;
              }
            }
          }
        }
      },
    },
  ],
} as const;

define/Stat|{ name, size }: { name: string; size: (number | undefined) }|
  if=size !== undefined
    const/{ value, unit }=bytesToUnits(size)
    div
      -- ${name}
      span class=styles.statEquals -- =
      strong -- ${value}
      small -- ${unit}

const/selectedFile=(input.files[input.selected]?.path as string | undefined)
let/outputType="preview"
let/linkCopied=false
let/codeCopied=false
let/consoleOpen=false
workspace/ws
let-debounce/files=input.files
const/showPreview=!(
  outputType !== "preview" ||
  ws.buildErrors ||
  ws.runtimeErrors
)
let-search-param/debugParam key="debug"
let/debug=!!debugParam
  ,valueChange(debug) {
    debugParam = debug ? "1" : null;
  }

let-search-param/fullscreenParam key="fullscreen"
let/fullscreen=!!fullscreenParam
  ,valueChange(fullscreen) {
    fullscreenParam = fullscreen ? "1" : null;
  }
script -- document.documentElement.classList.toggle("fullscreen", fullscreen);
html-script --
  ${
    `document.documentElement.classList.toggle("fullscreen",new URLSearchParams(location.search).get("fullscreen")=="1");`
  };

const/outputLang=(
  outputType === "previewCSS"
    ? "css"
    : outputType === "previewHTML"
      ? "html"
      : "js"
)
const/compiledCode=(() => {
  if (selectedFile) {
    switch (outputType) {
      case "dom": {
        return debug
          ? ws.markoCompiled?.dom?.[`/${selectedFile}`]?.code
          : ws.previewModules?.[`/${selectedFile}`]?.code;
      }
      case "html":
        return ws.markoCompiled?.html?.[`/${selectedFile}`]?.code;
      case "previewJS":
        return ws.previewJS;
      case "previewCSS":
        return ws.previewCSS;
      case "previewHTML":
        return ws.previewHTML;
    }
  }
})()

div class=styles.result
  div class=styles.controls
    div class=styles.control
      select value:=outputType
        optgroup label="Page"
          option value="preview" -- Preview
          option value="previewJS" -- Bundled JS
          option value="previewCSS" -- Bundled CSS
          option value="previewHTML" -- Rendered HTML
        optgroup label="Compiled"
          option value="dom" -- Client JS
          option value="html" -- Server JS
    div class=styles.control
      label class=styles.checkbox
        -- Debug
        input type="checkbox" checked:=debug
    span class=styles.version -- v${markoVersion}

  div class=styles.output
    if=ws.buildErrors
      error-output errors=ws.buildErrors files=(ws.fs?.files) kind="build"
    else if=outputType !== "preview"
      div
        ,id=styles.compiled
        ,contenteditable="true"
        ,onbeforeinput="return false" --
        ${" "}$!{
          highlighter.codeToHtml(
            compiledCode || (outputLang === "html" ? "" : "/* empty */"),
            {
              ...compiledHighlightOpts,
              lang: outputLang,
            },
          )
        }
    else if=ws.runtimeErrors
      error-output errors=ws.runtimeErrors files=(ws.fs?.files) kind="runtime"

    iframe/$frame
      ,title="code output"
      ,class={
        [styles.hide]: !showPreview,
      }
    if=files.length
      script -- update($signal, $frame(), files, !debug);

    if=showPreview && !ws.previewReady
      div class=styles.loading role="status"
        span -- setting up

    a class=styles.markoLogo href="https://markojs.com" title="Built with Marko"
    if=outputType !== "preview"
      button
        ,type="button"
        ,title=(codeCopied ? "Copied!" : "Copy code")
        ,class=styles.action
        ,onClick() {
          navigator.clipboard?.writeText(compiledCode || "").catch(() => {});
          codeCopied = true;
        }
        fa-icon=(codeCopied ? faCheck : faCopy)
          ,class=codeCopied && styles.copied
          ,onAnimationEnd() {
            codeCopied = false;
          }
  div class=styles.console
    div class=styles.consoleBar
      button
        ,type="button"
        ,class=styles.consoleToggle
        ,aria-expanded=(consoleOpen ? "true" : "false")
        ,onClick() {
          consoleOpen = !consoleOpen;
        }
        fa-icon=(consoleOpen ? faChevronDown : faChevronUp)
        span -- Console
        if=(ws.logs?.length)
          span class=styles.consoleCount -- ${ws.logs.length}
      if=(ws.logs?.length)
        button
          ,type="button"
          ,title="Clear console"
          ,class=styles.consoleClear
          ,onClick() {
            clearLogs();
          }
          fa-icon=faTrashCan
      if=outputType === "preview"
        div class=styles.stats
          Stat name="js" size=(ws.stats?.script?.gzip)
          Stat name="css" size=(ws.stats?.style?.gzip)
          Stat name="html" size=(ws.stats?.markup?.gzip)
        button
          ,title=(linkCopied ? "Copied!" : "Copy share link")
          ,class=[styles.previewControl, styles.shareControl]
          ,onClick() {
            navigator.clipboard?.writeText(location.href).catch(() => {});
            linkCopied = true;
          }
          fa-icon=(linkCopied ? faCheck : faLink)
            ,class=linkCopied && styles.copied
            ,onAnimationEnd() {
              linkCopied = false;
            }
        let/spinning=false
        button
          ,title="Reload preview"
          ,class=styles.previewControl
          ,onClick() {
            $frame().replaceWith($frame());
            spinning = true;
          }
          fa-icon=faRotateRight
            ,class=spinning && styles.spin
            ,onAnimationEnd() {
              spinning = false;
            }
        button
          ,title=(fullscreen ? "Leave fullscreen" : "Expand to fullscreen")
          ,class=styles.previewControl
          ,onClick() {
            fullscreen = !fullscreen;
          }
          fa-icon=(fullscreen ? faCompress : faExpand)
      else if=outputType === "previewJS"
        div class=styles.stats
          Stat name="size" size=(ws.stats?.script?.size)
          Stat name="gzip" size=(ws.stats?.script?.gzip)
      else if=outputType === "previewCSS"
        div class=styles.stats
          Stat name="size" size=(ws.stats?.style?.size)
          Stat name="gzip" size=(ws.stats?.style?.gzip)
    if=consoleOpen
      div class=styles.consoleBody
        if=(ws.logs?.length)
          for|log| of=ws.logs
            div class=styles.logEntry data-method=log.method
              span class=styles.logNs data-ns=log.ns -- ${log.ns}
              span class=styles.logText -- ${log.text}
        else
          div class=styles.consoleEmpty -- No console output yet.
